import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Subscription {
  id: string;
  user_id: string;
  plan_type: 'free' | 'pro';
  status: 'active' | 'canceled' | 'past_due';
  current_period_end: string | null;
}

interface Usage {
  qr_codes: number;
  short_links: number;
  bio_links: number;
}

interface SubscriptionData {
  subscription: Subscription | null;
  usage: Usage;
}

export function useSubscription() {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-subscription`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const canCreateQR = () => {
    if (!data) return false;
    if (data.subscription?.plan_type === 'pro') return true;
    return data.usage.qr_codes < 5;
  };

  const canCreateLink = () => {
    if (!data) return false;
    if (data.subscription?.plan_type === 'pro') return true;
    return data.usage.short_links < 10;
  };

  const canCreateBio = () => {
    if (!data) return false;
    if (data.subscription?.plan_type === 'pro') return true;
    return data.usage.bio_links < 3;
  };

  return {
    subscription: data?.subscription,
    usage: data?.usage,
    loading,
    canCreateQR,
    canCreateLink,
    canCreateBio,
    refetch: fetchSubscription,
  };
}
